import { StepAuthoringCatalog } from './workflow-step-composition';
import { WorkflowCapabilityCatalogEntry } from './workflow-capability-authoring';
import { WorkflowAutomationCatalog } from './workflow-automation-authoring';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppApis } from '@app/core/APIs/app-apis';
import { browserAssuranceRequestHeader } from '@app/core/services/auth.service';
import { map, Observable } from 'rxjs';
import type { WorkflowDefinitionVersionDetail } from './platform-workflow-registry-diagnostics.models';
import type { WorkflowFormLookupBinding } from '@app/shared/workflow-commands/workflow-form-lookup-contract';
import type { WorkflowLookupSourceOwner, WorkflowLookupSourcePage, WorkflowLookupSourceQuery, WorkflowLookupSourceRead } from './workflow-lookup-source-authoring.models';
import {
  WorkflowDefinitionActivationCommand,
  WorkflowDefinitionActivationDetail,
  WorkflowDefinitionActivationPage,
  WorkflowDefinitionActivationQuery,
  WorkflowDefinitionAuthoringReferences,
  WorkflowDefinitionCatalogPage,
  WorkflowDefinitionCatalogQuery,
  WorkflowDefinitionFamilyPage,
  WorkflowDefinitionFamilyQuery,
  WorkflowDefinitionFamilyVisibility,
  WorkflowDefinitionFamilyVisibilityCommand,
  WorkflowDefinitionDraftCloneCommand,
  WorkflowDefinitionDraftComparison,
  WorkflowDefinitionDraftCreateCommand,
  WorkflowDefinitionDraftDetail,
  WorkflowDefinitionDraftPage,
  WorkflowDefinitionDraftQuery,
  WorkflowDefinitionDraftUpdateCommand,
  WorkflowDefinitionDraftValidationResult,
  WorkflowDefinitionLifecycleDetail,
  WorkflowDefinitionPublishCommand,
  WorkflowDefinitionRetireCommand,
  WorkflowDefinitionRollbackCommand,
  WorkflowDefinitionMakeCurrentCommand,
  WorkflowDefinitionVersionChangePreview,
  WorkflowDefinitionUsageImpact,
} from './platform-workflow-definition-lifecycle.models';

const quietHeaders = new HttpHeaders({
  Accept: 'application/json, text/plain, */*',
  'Content-Type': 'application/json',
  'skip-error-interceptor': 'yes',
  'skip-loading-interceptor': 'yes',
});

const quietHttpOptions = {
  headers: quietHeaders,
  withCredentials: true,
};

/**
 * One user-confirmed mutation intent. Retain and reuse this object when an
 * uncertain transport outcome is retried so the API can replay the original
 * result instead of executing the command again.
 */
export interface WorkflowDefinitionMutationIntent {
  readonly idempotencyKey: string;
}

export function createWorkflowDefinitionMutationIntent(): WorkflowDefinitionMutationIntent {
  return { idempotencyKey: newIdempotencyKey() };
}

/**
 * Typed client for the governed Phase 2 workflow-definition lifecycle.
 *
 * Publication and activation use the normal authenticated session and their permissions.
 * Retirement and rollback use trusted evidence in the authenticated principal
 * after the deployment's browser/identity-provider verification flow completes.
 */
export interface WorkflowFoundationExampleInfo {
  enabled: boolean;
  statusCode: 'available' | 'disabled' | 'unavailable';
  displayName: string;
  workflowCode: string;
  draftCode: string;
  draftId: number | null;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class PlatformWorkflowDefinitionLifecycleService {
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);

  getLocalExample(): Observable<WorkflowFoundationExampleInfo> {
    return this.http.get<WorkflowFoundationExampleInfo>(
      this.apis.core.manual.getPlatformWorkflowLocalExample(),
      quietHttpOptions,
    );
  }

  getPublishedVersion(
    workflowCode: string,
    definitionVersionCode: string,
  ): Observable<WorkflowDefinitionVersionDetail> {
    return this.http
      .get<
        | WorkflowDefinitionVersionDetail
        | { data: WorkflowDefinitionVersionDetail }
      >(this.apis.core.manual.getPlatformWorkflowDefinition(workflowCode, definitionVersionCode), quietHttpOptions)
      .pipe(map((result) => ('data' in result ? result.data : result)));
  }

  getCatalog(
    query: WorkflowDefinitionCatalogQuery,
  ): Observable<WorkflowDefinitionCatalogPage> {
    const historyQuery = { ...query };
    delete historyQuery.includeHidden;
    return this.http.get<WorkflowDefinitionCatalogPage>(
      this.apis.core.manual.getPlatformWorkflowDefinitionCatalog(historyQuery),
      quietHttpOptions,
    );
  }

  getFamilies(
    query: WorkflowDefinitionFamilyQuery,
  ): Observable<WorkflowDefinitionFamilyPage> {
    return this.http.get<WorkflowDefinitionFamilyPage>(
      this.apis.core.manual.getPlatformWorkflowDefinitionFamilies(query),
      quietHttpOptions,
    );
  }

  getFamilyVisibility(
    workflowCode: string,
  ): Observable<WorkflowDefinitionFamilyVisibility> {
    return this.http.get<WorkflowDefinitionFamilyVisibility>(
      this.apis.core.manual.getPlatformWorkflowFamilyVisibility(workflowCode),
      quietHttpOptions,
    );
  }

  updateFamilyVisibility(
    workflowCode: string,
    eTag: string,
    command: WorkflowDefinitionFamilyVisibilityCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionFamilyVisibility> {
    return this.http.put<WorkflowDefinitionFamilyVisibility>(
      this.apis.core.manual.getPlatformWorkflowFamilyVisibility(workflowCode),
      command,
      this.mutationOptions(eTag, intent),
    );
  }

  getCapabilities(document?: {
    workflowCode: string;
    packReference?: {
      code: string;
      version: number;
      manifestHash: string;
    } | null;
  }): Observable<WorkflowCapabilityCatalogEntry[]> {
    const params: Record<string, string> = {};
    if (document) params['workflowCode'] = document.workflowCode;
    if (document?.packReference) {
      params['packCode'] = document.packReference.code;
      params['packVersion'] = String(document.packReference.version);
      params['packHash'] = document.packReference.manifestHash;
    }
    return this.http.get<WorkflowCapabilityCatalogEntry[]>(
      this.apis.core.manual.getPlatformCapabilities(),
      { ...quietHttpOptions, params },
    );
  }

  getStepAuthoringCatalog(document: {
    workflowCode: string;
    scopeTypeCode?: string;
    scopeKey?: string | null;
    packReference?: {
      code: string;
      version: number;
      manifestHash: string;
    } | null;
  }): Observable<StepAuthoringCatalog> {
    const params: Record<string, string> = {
      workflowCode: document.workflowCode,
    };
    if (document.scopeTypeCode)
      params['scopeTypeCode'] = document.scopeTypeCode;
    if (document.scopeKey) params['scopeKey'] = document.scopeKey;
    if (document.packReference) {
      params['packCode'] = document.packReference.code;
      params['packVersion'] = String(document.packReference.version);
      params['packHash'] = document.packReference.manifestHash;
    }
    return this.http.get<StepAuthoringCatalog>(
      this.apis.core.manual.getPlatformCapabilities() + '/step-authoring',
      { ...quietHttpOptions, params },
    );
  }

  getAutomationAuthoringCatalog(document: {
    workflowCode: string;
    packReference?: {
      code: string;
      version: number;
      manifestHash: string;
    } | null;
  }): Observable<WorkflowAutomationCatalog> {
    const params: Record<string, string> = {
      workflowCode: document.workflowCode,
    };
    if (document.packReference) {
      params['packCode'] = document.packReference.code;
      params['packVersion'] = String(document.packReference.version);
      params['packHash'] = document.packReference.manifestHash;
    }
    return this.http.get<WorkflowAutomationCatalog>(
      this.apis.core.manual.getPlatformCapabilities() + '/automation-authoring',
      { ...quietHttpOptions, params },
    );
  }

  getAutomatedActivityCatalog(document: {
    workflowCode: string;
    packReference?: {
      code: string;
      version: number;
      manifestHash: string;
    } | null;
  }): Observable<WorkflowAutomationCatalog> {
    const params: Record<string, string> = {
      workflowCode: document.workflowCode,
    };
    if (document.packReference) {
      params['packCode'] = document.packReference.code;
      params['packVersion'] = String(document.packReference.version);
      params['packHash'] = document.packReference.manifestHash;
    }
    return this.http.get<WorkflowAutomationCatalog>(
      this.apis.core.manual.getPlatformCapabilities() + '/automated-activities',
      { ...quietHttpOptions, params },
    );
  }

  getTimerAuthoringCatalog(document: {
    workflowCode: string;
    packReference?: {
      code: string;
      version: number;
      manifestHash: string;
    } | null;
  }): Observable<unknown> {
    const params: Record<string, string> = {
      workflowCode: document.workflowCode,
    };
    if (document.packReference) {
      params['packCode'] = document.packReference.code;
      params['packVersion'] = String(document.packReference.version);
      params['packHash'] = document.packReference.manifestHash;
    }
    return this.http.get<unknown>(
      this.apis.core.manual.getPlatformCapabilities() + '/timer-authoring',
      { ...quietHttpOptions, params },
    );
  }

  getAuthoringReferences(): Observable<WorkflowDefinitionAuthoringReferences> {
    return this.http.get<WorkflowDefinitionAuthoringReferences>(
      this.apis.core.manual.getPlatformWorkflowDefinitionAuthoringReferences(),
      quietHttpOptions,
    );
  }

  createDraft(
    command: WorkflowDefinitionDraftCreateCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionDraftDetail> {
    return this.http.post<WorkflowDefinitionDraftDetail>(
      this.apis.core.manual.postPlatformWorkflowDefinitionDrafts(),
      command,
      this.mutationOptions(undefined, intent),
    );
  }

  getDrafts(
    query: WorkflowDefinitionDraftQuery,
  ): Observable<WorkflowDefinitionDraftPage> {
    return this.http.get<WorkflowDefinitionDraftPage>(
      this.apis.core.manual.getPlatformWorkflowDefinitionDrafts(query),
      quietHttpOptions,
    );
  }

  cloneVersion(
    workflowCode: string,
    definitionVersionCode: string,
    command: WorkflowDefinitionDraftCloneCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionDraftDetail> {
    return this.http.post<WorkflowDefinitionDraftDetail>(
      this.apis.core.manual.postPlatformWorkflowDefinitionCloneDraft(
        workflowCode,
        definitionVersionCode,
      ),
      command,
      this.mutationOptions(undefined, intent),
    );
  }

  getDraft(draftId: number): Observable<WorkflowDefinitionDraftDetail> {
    return this.http.get<WorkflowDefinitionDraftDetail>(
      this.apis.core.manual.getPlatformWorkflowDefinitionDraft(draftId),
      quietHttpOptions,
    );
  }

  /** Side-effect-free authoring metadata; client actor/session identity is deliberately excluded. */
  searchLookupSources(
    owner: WorkflowLookupSourceOwner,
    query: WorkflowLookupSourceQuery,
  ): Observable<WorkflowLookupSourcePage> {
    return this.http.post<WorkflowLookupSourcePage>(
      this.apis.core.manual.getPlatformWorkflowDefinitionDraft(owner.draftId) +
        '/lookup-sources/search',
      {
        expectedRevision: owner.revision,
        expectedETag: owner.etag,
        context: {
          workflowCode: owner.context.workflowCode,
          subjectTypeCode: owner.context.subjectTypeCode,
          scopeTypeCode: owner.context.scopeTypeCode,
          scopeKey: owner.context.scopeKey,
        },
        search: query.search,
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
      },
      quietHttpOptions,
    );
  }

  resolveLookupSource(
    owner: WorkflowLookupSourceOwner,
    binding: WorkflowFormLookupBinding,
  ): Observable<WorkflowLookupSourceRead> {
    return this.http.post<WorkflowLookupSourceRead>(
      this.apis.core.manual.getPlatformWorkflowDefinitionDraft(owner.draftId) +
        '/lookup-sources/resolve',
      {
        expectedRevision: owner.revision,
        expectedETag: owner.etag,
        context: {
          workflowCode: owner.context.workflowCode,
          subjectTypeCode: owner.context.subjectTypeCode,
          scopeTypeCode: owner.context.scopeTypeCode,
          scopeKey: owner.context.scopeKey,
        },
        binding: { ...binding },
      },
      quietHttpOptions,
    );
  }

  updateDraft(
    draftId: number,
    eTag: string,
    command: WorkflowDefinitionDraftUpdateCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionDraftDetail> {
    return this.http.put<WorkflowDefinitionDraftDetail>(
      this.apis.core.manual.putPlatformWorkflowDefinitionDraft(draftId),
      command,
      this.mutationOptions(eTag, intent),
    );
  }

  validateDraft(
    draftId: number,
    eTag: string,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionDraftValidationResult> {
    return this.http.post<WorkflowDefinitionDraftValidationResult>(
      this.apis.core.manual.postPlatformWorkflowDefinitionDraftValidate(
        draftId,
      ),
      null,
      this.mutationOptions(eTag, intent),
    );
  }

  compareDraft(
    draftId: number,
    definitionVersionId?: number,
  ): Observable<WorkflowDefinitionDraftComparison> {
    const query =
      definitionVersionId === undefined ? undefined : { definitionVersionId };
    return this.http.get<WorkflowDefinitionDraftComparison>(
      this.apis.core.manual.getPlatformWorkflowDefinitionDraftCompare(
        draftId,
        query,
      ),
      quietHttpOptions,
    );
  }

  publishDraft(
    draftId: number,
    eTag: string,
    command: WorkflowDefinitionPublishCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionLifecycleDetail> {
    return this.http.post<WorkflowDefinitionLifecycleDetail>(
      this.apis.core.manual.postPlatformWorkflowDefinitionDraftPublish(draftId),
      command,
      this.mutationOptions(eTag, intent),
    );
  }

  activateVersion(
    definitionVersionId: number,
    command: WorkflowDefinitionActivationCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionActivationDetail> {
    return this.http.post<WorkflowDefinitionActivationDetail>(
      this.apis.core.manual.postPlatformWorkflowDefinitionActivate(
        definitionVersionId,
      ),
      command,
      this.mutationOptions(undefined, intent),
    );
  }

  retireActivation(
    activationId: number,
    eTag: string,
    command: WorkflowDefinitionRetireCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionActivationDetail> {
    return this.http.post<WorkflowDefinitionActivationDetail>(
      this.apis.core.manual.postPlatformWorkflowDefinitionActivationRetire(
        activationId,
      ),
      command,
      this.mutationOptions(eTag, intent, true),
    );
  }

  rollbackToVersion(
    definitionVersionId: number,
    command: WorkflowDefinitionRollbackCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionActivationDetail> {
    return this.http.post<WorkflowDefinitionActivationDetail>(
      this.apis.core.manual.postPlatformWorkflowDefinitionRollback(
        definitionVersionId,
      ),
      command,
      this.mutationOptions(undefined, intent, true),
    );
  }

  getVersionChangePreview(
    definitionVersionId: number,
  ): Observable<WorkflowDefinitionVersionChangePreview> {
    return this.http.get<WorkflowDefinitionVersionChangePreview>(
      this.apis.core.manual.getPlatformWorkflowDefinitionVersionChange(
        definitionVersionId,
      ),
      quietHttpOptions,
    );
  }

  makeCurrentVersion(
    definitionVersionId: number,
    command: WorkflowDefinitionMakeCurrentCommand,
    intent = createWorkflowDefinitionMutationIntent(),
  ): Observable<WorkflowDefinitionActivationDetail> {
    return this.http.post<WorkflowDefinitionActivationDetail>(
      this.apis.core.manual.postPlatformWorkflowDefinitionMakeCurrent(
        definitionVersionId,
      ),
      command,
      this.mutationOptions(undefined, intent),
    );
  }

  getUsageImpact(
    definitionVersionId: number,
  ): Observable<WorkflowDefinitionUsageImpact> {
    return this.http.get<WorkflowDefinitionUsageImpact>(
      this.apis.core.manual.getPlatformWorkflowDefinitionUsageImpact(
        definitionVersionId,
      ),
      quietHttpOptions,
    );
  }

  getActivations(
    definitionVersionId: number,
    query: WorkflowDefinitionActivationQuery,
  ): Observable<WorkflowDefinitionActivationPage> {
    return this.http.get<WorkflowDefinitionActivationPage>(
      this.apis.core.manual.getPlatformWorkflowDefinitionActivations(
        definitionVersionId,
        query,
      ),
      quietHttpOptions,
    );
  }

  private mutationOptions(
    eTag: string | undefined,
    intent: WorkflowDefinitionMutationIntent,
    useBrowserAssurance = false,
  ) {
    let headers = quietHeaders.set('Idempotency-Key', intent.idempotencyKey);
    if (eTag !== undefined) {
      headers = headers.set('If-Match', eTag);
    }
    if (useBrowserAssurance) {
      headers = headers.set(browserAssuranceRequestHeader, 'true');
    }

    return {
      headers,
      withCredentials: true,
    };
  }
}

function newIdempotencyKey(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } })
    .crypto;
  return cryptoApi?.randomUUID
    ? cryptoApi.randomUUID()
    : `workflow-definition-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
